import { useMutation, useQuery } from "@apollo/client";
import { useMemo } from "react";
import { gql, PasswordPolicyInput } from "./types";

type MinimalPasswordPolicy = {
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireNumber: boolean;
  requireSymbol: boolean;
  minLength: number;
};

const GET_POLICY = gql(/* GraphQL */ `
  query GetPasswordPolicy {
    getPasswordPolicy {
      requireUppercase
      requireLowercase
      requireNumber
      requireSymbol
      minLength
      rejectCommon
      forceResetEnabled
      forceResetDays
      disallowPreviousPasswords
    }
  }
`);

const UPDATE_POLICY = gql(/* GraphQL */ `
  mutation UpdatePasswordPolicy($input: PasswordPolicyInput!) {
    updatePasswordPolicy(input: $input) {
      id
    }
  }
`);

export function usePasswordPolicy() {
  const { data, loading, error, refetch } = useQuery(GET_POLICY);
  const [updatePolicy, { loading: updating }] = useMutation(UPDATE_POLICY);

  const savePolicy = async (input: PasswordPolicyInput) => {
    await updatePolicy({ variables: { input } });
    await refetch();
  };
  const policy = data?.getPasswordPolicy ?? null;

  const validatePassword = useMemo(() => {
    return (password: string): string | null => {
      if (!policy) return null;
      return validatePasswordAgainstPolicy(password, policy);
    };
  }, [policy]);

  return {
    policy,
    loading,
    error,
    savePolicy,
    updating,
    validatePassword,
  };
}

function validatePasswordAgainstPolicy(
  password: string,
  policy: MinimalPasswordPolicy,
): string | null {
  if (password.length < policy.minLength) {
    return `Password must be at least ${policy.minLength} characters`;
  }
  if (policy.requireUppercase && !/[A-Z]/.test(password)) {
    return "Password must include at least one uppercase letter";
  }
  if (policy.requireLowercase && !/[a-z]/.test(password)) {
    return "Password must include at least one lowercase letter";
  }
  if (policy.requireNumber && !/\d/.test(password)) {
    return "Password must include at least one number";
  }
  // eslint-disable-next-line no-useless-escape
  if (policy.requireSymbol && !/[!@#$%^&*(),.?\":{}|<>]/.test(password)) {
    return "Password must include at least one symbol";
  }
  return null;
}
